// services/apiService.ts
import type School from '@/models/school';
import type Course from '@/models/course';
import httpClient from '@/common/httpClient';

/**
 * CA_API base URL for the Arizona tenant.
 *
 * Deliberately not VITE_CA_API_URL: that one points at api.curricularanalytics.com
 * for LARX tenants, whose routes also sit under an /optimize prefix. Arizona talks
 * to api.azdegreeplans.com with no prefix. Set VITE_AZ_CA_API_URL to override,
 * e.g. http://localhost:8080 when running CA_API locally.
 */
const CA_API_BASE_URL = (
  import.meta.env.VITE_AZ_CA_API_URL || 'https://api.azdegreeplans.com'
).replace(/\/+$/, '');

/**
 * Strip UTF-8 BOM if present.
 */
function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, '');
}

/**
 * Minimal CSV parser with support for:
 * - quoted fields (")
 * - commas inside quoted fields
 * - escaped quotes ("")
 * - CRLF/LF newlines
 * - ignoring blank lines
 *
 * Returns: Array<Record<header, value>>
 */
function parseCsv(text: string): Array<Record<string, string>> {
  text = stripBom(text);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };

  const rowIsAllEmpty = (r: string[]) => r.every((cell) => (cell ?? '').trim() === '');

  const pushRow = () => {
    if (row.length === 0 || rowIsAllEmpty(row)) {
      row = [];
      return;
    }
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        const next = text[i + 1];
        if (next === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === ',') {
      pushField();
      continue;
    }

    if (ch === '\n') {
      pushField();
      pushRow();
      continue;
    }

    if (ch === '\r') continue;

    field += ch;
  }

  // flush last row
  pushField();
  pushRow();

  if (rows.length === 0) return [];

  const headers = rows[0].map((h) => stripBom((h ?? '').trim()));
  const dataRows = rows.slice(1);

  return dataRows.map((r) => {
    const obj: Record<string, string> = {};
    for (let c = 0; c < headers.length; c++) {
      const key = headers[c] || `__col_${c}`; // handles trailing comma header like OTHER,
      obj[key] = (r[c] ?? '').trim();
    }
    return obj;
  });
}

/**
 * Case-insensitive + BOM-safe field getter.
 */
function getField(r: Record<string, string>, key: string): string {
  if (r[key] != null) return r[key];

  const target = key.toLowerCase();
  for (const k of Object.keys(r)) {
    const normalized = k.replace(/^\uFEFF/, '').trim().toLowerCase();
    if (normalized === target) return r[k] ?? '';
  }
  return '';
}

/**
 * Fetch courses for a specific school from local public CSV:
 * /public/data/<school.slug>/CourseCatalog.csv -> /data/<school.slug>/CourseCatalog.csv
 */
const courseCatalogCache = new Map<string, Course[]>();

/**
 * Slugs whose courses live in LARX rather than in a bundled CourseCatalog.csv.
 * These schools ship no CSV, so their picker is populated through CA_API.
 */
const LARX_SOURCED_SCHOOL_SLUGS = ['tohono-oodham-community-college'];

export function usesLarxCourseCatalog(school: School): boolean {
  return LARX_SOURCED_SCHOOL_SLUGS.includes((school as any)?.slug);
}

export async function fetchSchoolCourses(school: School): Promise<Course[]> {
  const slug = (school as any)?.slug;
  if (!slug) throw new Error(`Missing school.slug for ${school?.name ?? 'unknown school'}`);

  if (usesLarxCourseCatalog(school)) {
    return fetchSchoolCoursesFromApi(school);
  }

  const url = `/data/${slug}/CourseCatalog.csv`;

  // cache hit
  const cached = courseCatalogCache.get(url);
  if (cached) return cached;

  const resp = await fetch(url, { cache: 'no-store' });
  if (!resp.ok) {
    throw new Error(`Failed to fetch course catalog CSV (${resp.status}): ${url}`);
  }

  const contentType = resp.headers.get('content-type') || '';
  const text = await resp.text();

  // If the server returned index.html (router fallback), fail loudly
  const looksHtml =
    contentType.includes('text/html') ||
    text.trim().startsWith('<!DOCTYPE html') ||
    text.trim().startsWith('<html');

  if (looksHtml) {
    throw new Error(`Expected CSV but got HTML from ${url}. Check public/data/${slug}/CourseCatalog.csv path.`);
  }

  const rows = parseCsv(text);

  const courses: Course[] = rows
    .map((r) => {
      const prefix = getField(r, 'PREFIX').trim();
      const num = getField(r, 'NUM').trim();
      const title = getField(r, 'TITLE').trim();
      const courseIdRaw = getField(r, 'COURSE_ID').trim();
      const description = getField(r, 'COURSE_DESCRIPTION').trim();

      const minCreditsRaw = getField(r, 'CREDITS_MINIMUM').trim();
      const maxUnitsRaw = getField(r, 'MAX_UNITS').trim();
      const minimum_credits = minCreditsRaw ? Number(minCreditsRaw) : 0;
      const maximum_credits = maxUnitsRaw ? Number(maxUnitsRaw) : minimum_credits;

      const code = prefix && num ? `${prefix} ${num}` : `${prefix}${num}`.trim();
      const fullName = code && title ? `${code} - ${title}` : (title || code);

      return {
        // required by Course type
        minimum_credits,
        maximum_credits,

        // fields your UI uses
        id: courseIdRaw ? Number(courseIdRaw) : undefined,
        code,
        full_number: code,
        name: title,
        title,
        full_name: fullName,

        description,
        course_description: description,
        catalog_description: description,

      } as Course;
    })
    // Remove blank artifacts (e.g., trailing newline)
    .filter((c) => (c.full_name ?? '').trim() !== '');

  // Nice ordering in SearchSelect
  courses.sort((a: any, b: any) => (a.full_name || '').localeCompare(b.full_name || ''));

  courseCatalogCache.set(url, courses);
  return courses;
}

/**
 * Generate transfer completion plan
 */
export async function generateCompletionPlan(data: {
  source_school: School;
  dest_program: any;
  taken_courses: Course[];
}): Promise<any> {
  const url = `${CA_API_BASE_URL}/completion_plan`;

  const { data: resp } = await httpClient.post(url, data, {
    headers: { 'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY },
  });
  return resp;
}

/**
 * Generate two-to-four year transfer plan
 */
export async function generateTwoToFourPlan(data: {
  source_school: School;
  source_program: any;
  dest_school: School;
  dest_program: any;
}): Promise<any> {
  const url = `${CA_API_BASE_URL}/two_to_four_plan`;

  const { data: resp } = await httpClient.post(url, data, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY,
    },
  });
  return resp;
}

/**
 * Fetch a LARX-sourced school's courses from CA_API.
 *
 * Schools whose curriculum lives in LARX ship no CourseCatalog.csv. The browser
 * cannot read LARX itself — degreeReqsApi sends no credentials, and giving it the
 * M2M secret would publish it in the bundle — so CA_API, which already holds
 * those credentials, fetches the list on our behalf.
 *
 * Returns courses whose `id` is the CohortETL course id, matching what the CSV
 * path produces, so the rest of the pipeline cannot tell the two apart.
 */
export async function fetchSchoolCoursesFromApi(school: School): Promise<Course[]> {
  const slug = (school as any)?.slug;
  if (!slug) throw new Error(`Missing school.slug for ${school?.name ?? 'unknown school'}`);

  const { data } = await httpClient.post(
    `${CA_API_BASE_URL}/school_courses`,
    { school: { id: (school as any)?.id, name: school?.name, slug } },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY,
      },
    }
  );

  return ((data?.courses ?? []) as any[]).map((c) => {
    const code = [c.prefix, c.num].filter(Boolean).join(' ').trim();
    const credits = typeof c.credit_hours === 'number' ? c.credit_hours : 0;
    return {
      minimum_credits: credits,
      maximum_credits: credits,
      id: c.id,
      code,
      full_number: code,
      name: c.name,
      title: c.name,
      full_name: code && c.name ? `${code} - ${c.name}` : (c.name || code),
      description: '',
    } as unknown as Course;
  });
}

/**
 * Get all available schools
 */
export function getSchools(): {
  startingSchools: School[];
  destinationSchools: School[];
} {
  const startingSchools = [
    { id: 3056, name: 'Pima Community College', slug: 'pima-community-college', disable: false },
    { id: 4657, name: 'Maricopa Community College', slug: 'maricopa-community-college', disable: true },
    { id: 4658, name: 'Eastern Arizona College', slug: 'eastern-arizona-college', disable: true },
    { id: 1, name: 'Tohono O’odham Community College', slug: 'tohono-oodham-community-college', disable: true },
    { id: 2, name: 'Cochise Community College', slug: 'cochise-community-college', disable: true },
    { id: 3, name: "Arizona Western College", slug: "arizona-western-college", disable: true }
  ];

  const destinationSchools = [
    { id: 4036, name: 'University of Arizona', slug: 'university-of-arizona', disable: false },
  ];

  return { startingSchools, destinationSchools };
}